import React, { useEffect, useState } from 'react';
import { EntityList } from './EntityList';
import { EntityDetail } from './EntityDetail';
import { SiteDetail } from './SiteDetail';
import { ChevronRightIcon } from 'lucide-react';
export function EntityManagement({
  locale
}) {
  const [view, setView] = useState('list');
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [selectedSite, setSelectedSite] = useState(null);
  const [entities, setEntities] = useState([{
    id: 'ent-001',
    name: 'Global Technologies Inc.',
    code: 'GTI',
    country: 'US',
    currency: 'USD',
    activeFrom: '2020-01-01',
    activeTo: '2030-12-31',
    isActive: true,
    sites: [{
      id: 'site-001',
      name: 'New York HQ',
      code: 'NY-HQ',
      country: 'US',
      currency: 'USD',
      activeFrom: '2020-01-01',
      activeTo: '2030-12-31',
      isActive: true,
      contacts: [{
        id: 'con-001',
        name: 'John Smith',
        email: 'john.smith@example.com',
        phone: '+1 212-555-1234',
        role: 'Finance',
        isPrimary: true
      }],
      addresses: [{
        id: 'addr-001',
        line1: '123 Broadway',
        line2: 'Floor 25',
        city: 'New York',
        state: 'NY',
        postcode: '10001',
        country: 'US',
        addressType: 'Physical',
        isPrimary: true
      }]
    }, {
      id: 'site-002',
      name: 'San Francisco Office',
      code: 'SF-01',
      country: 'US',
      currency: 'USD',
      activeFrom: '2021-03-15',
      activeTo: '2030-12-31',
      isActive: true,
      contacts: [],
      addresses: []
    }],
    contacts: [{
      id: 'con-002',
      name: 'Jane Doe',
      email: 'jane.doe@example.com',
      phone: '+1 415-555-6789',
      role: 'Admin',
      isPrimary: true
    }],
    addresses: [{
      id: 'addr-002',
      line1: '456 Market Street',
      line2: 'Suite 300',
      city: 'San Francisco',
      state: 'CA',
      postcode: '94105',
      country: 'US',
      addressType: 'Billing',
      isPrimary: true
    }]
  }, {
    id: 'ent-002',
    name: 'European Operations Ltd',
    code: 'EOL',
    country: 'GB',
    currency: 'GBP',
    activeFrom: '2021-05-01',
    activeTo: '2029-12-31',
    isActive: true,
    sites: [],
    contacts: [],
    addresses: []
  }]);
  const handleEntitySelect = entity => {
    setSelectedEntity(entity);
    setView('entity-detail');
  };
  const handleSiteSelect = site => {
    if (!selectedEntity) {
      setView('list');
      return;
    }
    setSelectedSite(site);
    setView('site-detail');
  };
  const handleCreateEntity = () => {
    setSelectedEntity(null);
    setView('entity-detail');
  };
  const handleCreateSite = () => {
    if (!selectedEntity) {
      setView('list');
      return;
    }
    setSelectedSite(null);
    setView('site-detail');
  };
  const handleSaveEntity = entityData => {
    if (entityData.id) {
      // Update existing entity
      setEntities(entities.map(entity => entity.id === entityData.id ? {
        ...entityData
      } : entity));
    } else {
      // Create new entity
      const newEntity = {
        ...entityData,
        id: `ent-${new Date().getTime()}`,
        sites: [],
        contacts: [],
        addresses: []
      };
      setEntities([...entities, newEntity]);
    }
    setView('list');
  };
  const handleSaveSite = siteData => {
    if (siteData.id) {
      // Update existing site
      setEntities(entities.map(entity => {
        if (entity.id === selectedEntity.id) {
          return {
            ...entity,
            sites: entity.sites.map(site => site.id === siteData.id ? {
              ...siteData
            } : site)
          };
        }
        return entity;
      }));
    } else {
      // Create new site
      const newSite = {
        ...siteData,
        id: `site-${new Date().getTime()}`,
        contacts: [],
        addresses: []
      };
      setEntities(entities.map(entity => {
        if (entity.id === selectedEntity.id) {
          return {
            ...entity,
            sites: [...entity.sites, newSite]
          };
        }
        return entity;
      }));
    }
    setView('entity-detail');
  };
  const handleBackToList = () => {
    setView('list');
    setSelectedEntity(null);
    setSelectedSite(null);
  };
  const handleBackToEntity = () => {
    if (!selectedEntity) {
      setView('list');
      return;
    }
    setView('entity-detail');
    setSelectedSite(null);
  };
  const getBreadcrumbs = () => {
    if (view === 'list') {
      return [{
        label: 'Entities',
        onClick: handleBackToList
      }];
    } else if (view === 'entity-detail') {
      return [{
        label: 'Entities',
        onClick: handleBackToList
      }, {
        label: selectedEntity ? selectedEntity.name : 'New Entity'
      }];
    } else if (view === 'site-detail') {
      // Add null checks for both selectedEntity and selectedSite
      if (!selectedEntity) {
        // If somehow we got here without an entity, go back to list
        setView('list');
        return [{
          label: 'Entities',
          onClick: handleBackToList
        }];
      }
      return [{
        label: 'Entities',
        onClick: handleBackToList
      }, {
        label: selectedEntity.name,
        onClick: handleBackToEntity
      }, {
        label: selectedSite ? selectedSite.name : 'New Site'
      }];
    }
    return [];
  };
  return <div className="space-y-6" data-id="element-15462">
      <div className="mb-8" data-id="element-15463">
        <h2 className="text-2xl font-bold text-gray-900" data-id="element-15464">
          Entity & Site Management
        </h2>
        <p className="mt-1 text-sm text-gray-500" data-id="element-15465">
          Manage your organization's entities and sites
        </p>
      </div>
      {/* Breadcrumbs */}
      <nav className="flex mb-4" aria-label="Breadcrumb" data-id="element-15466">
        <ol className="inline-flex items-center space-x-1 md:space-x-3" data-id="element-15467">
          {getBreadcrumbs().map((crumb, index) => <li key={index} className="inline-flex items-center" data-id="element-15468">
              {index > 0 && <ChevronRightIcon className="w-4 h-4 text-gray-400 mx-1" data-id="element-15469" />}
              {crumb.onClick ? <button onClick={crumb.onClick} className="inline-flex items-center text-sm font-medium text-indigo-600 hover:text-indigo-800" data-id="element-15470">
                  {crumb.label}
                </button> : <span className="text-sm font-medium text-gray-500" data-id="element-15471">
                  {crumb.label}
                </span>}
            </li>)}
        </ol>
      </nav>
      {view === 'list' && <EntityList entities={entities} onSelectEntity={handleEntitySelect} onCreateEntity={handleCreateEntity} locale={locale} data-id="element-15472" />}
      {view === 'entity-detail' && <EntityDetail entity={selectedEntity} onSave={handleSaveEntity} onCancel={handleBackToList} onSelectSite={handleSiteSelect} onCreateSite={handleCreateSite} locale={locale} data-id="element-15473" />}
      {view === 'site-detail' && <SiteDetail site={selectedSite} entity={selectedEntity} onSave={handleSaveSite} onCancel={handleBackToEntity} locale={locale} data-id="element-15474" />}
    </div>;
}